import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ColorRing } from 'react-loader-spinner';  // Import the spinner component

import {
  contactName,
  level,
  phoneFax,
  secondaryPhone,
  emailContact,
  description,
} from '../../config/contactFormDataConfig';
import { RenderFields, RenderFieldsDropdown } from './RenderFields';
import { editContact, deleteContact } from '../../services/contactService';
import { getContactLevelName } from '../../utils/contactUtils';
import useContactFormData from '../../hooks/useContactFormData';
import ContactAccordion from './ContactAccordion';

const ContactModal = ({ open, handleClose, contactData, handleDataChange, isLoading, setNext, setPrev, }) => {
  const [isEditable, setIsEditable] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { formData, handleChange, setFormData, validationErrors } = useContactFormData(contactData);
  const contactNameTitle = formData && formData.contactName ? formData.contactName : 'Contact';

  useEffect(() => {
    if (contactData) {
      setFormData(contactData);
    }
  }, [contactData, setFormData]);

  // Edit mode toggle
  const handleEditClick = () => setIsEditable(true);
  const handleCancelEdit = () => {
    setIsEditable(false);
    setFormData(contactData); // Reset form data to original contact data
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      Object.values(validationErrors).every((error) => !error) &&
      formData.contactName &&
      formData.phone1 &&
      formData.emailAddress
    );
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      alert('Please fix validation errors before saving.');
      return;
    }
    try {
      await editContact(contactData.contactId, formData);
      setIsEditable(false);
      handleDataChange(); // This will trigger the data refresh
    } catch (error) {
      console.error('Error editing contact:', error);
      alert('Error editing contact');
    }
  };

  const handleDeleteContact = async () => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }
    try {
      await deleteContact(contactData.contactId);
      setIsEditable(false);
      handleDataChange(); // This will trigger the data refresh
      handleClose();
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Error deleting contact');
    }
  };

  const handleModalClose = (event, reason) => {
    if (isEditable && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      // Prevent closing when in edit mode
      return;
    }
    setIsEditable(false);
    handleClose();
  };

  const handlePrev = useCallback(() => {
    setPrev();
    setIsEditable(false);
  }, [setPrev, setIsEditable]);

  const handleNext = useCallback(() => {
    setNext();
    setIsEditable(false);
  }, [setNext, setIsEditable]);

  return (
    <>
      {/* Navigation Buttons */}
      {open && !isEditable && (
        <>
          {/* Previous Button */}
          <button
            onClick={handlePrev}
            style={{
              position: 'fixed',
              left: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
          >
            <ArrowBackIcon />
          </button>
          {/* Next Button */}
          <button
            onClick={handleNext}
            style={{
              position: 'fixed',
              right: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
          >
            <ArrowForwardIcon />
          </button>
        </>
      )}
      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? '90%' : '1000px',
            maxHeight: '90vh',
            minHeight: '90vh',
            overflow: 'visible',
          },
        }}
        fullWidth={true}
        style={{ zIndex: 1400 }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center p-4">
            {/* Left section: Back button and title */}
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={handleModalClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
                aria-label="Back"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="text-2xl font-bold text-left">
                {isEditable
                  ? `Edit Contact - ${contactNameTitle}`
                  : `View Contact - ${contactNameTitle}`}
              </h2>
            </div>
            {/* Right section: Expand/Shrink Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>
          <div className="flex space-x-4 p-4">
            {isEditable ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-gray-300 text-sss-blue-sel font-bold rounded-md hover:bg-gray-400 transition duration-300 ease-in-out"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-yellow-500 font-bold rounded-md hover:bg-yellow-600 text-sss-blue-sel transition duration-300 ease-in-out"
                >
                  Save
                </button>
                <button
                  onClick={handleDeleteContact}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
                >
                  Delete
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleEditClick}
                  className="px-6 py-2 bg-yellow-500 font-bold rounded-md hover:bg-yellow-600 text-sss-blue-sel transition duration-300 ease-in-out"
                >
                  Edit
                </button>
              </>
            )}
          </div>
        </div>
        {/* Modal Content */}
        <DialogContent dividers style={{ minHeight: '500px' }}>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[500px]">
              <ColorRing
                height="200"
                width="200"
                ariaLabel="color-ring-loading"
                wrapperStyle={{}}
                wrapperClass="color-ring-wrapper"
                colors={['#033B5C', '#033B5C', '#033B5C', '#033B5C', '#033B5C', '#033B5C']}
              />
              <p>Loading Contact Details...</p>
            </div>
          ) : formData ? (
            <div className="space-y-4">
              <div className="flex flex-col gap-4">
                {/* Contact Name Section Accordion */}
                <ContactAccordion label="General">
                  <div className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-2`}>
                    <div className="w-1/2">
                      <RenderFields
                        fields={contactName}
                        formData={formData}
                        handleChange={handleChange}
                        readOnly={!isEditable}
                        validationErrors={validationErrors}
                      />
                    </div>
                    <div className="w-1/2">
                      <RenderFields
                        fields={description}
                        formData={formData}
                        handleChange={handleChange}
                        readOnly={!isEditable}
                        validationErrors={validationErrors}
                      />
                      {isEditable ? (
                        <RenderFieldsDropdown
                          fields={level}
                          formData={formData}
                          handleChange={handleChange}
                          validationErrors={validationErrors}
                        />
                      ) : (
                        <RenderFields
                          fields={level}
                          formData={formData}
                          readOnly={true}
                          fieldTransformers={{ contactLevel: getContactLevelName }}
                        />
                      )}
                    </div>
                  </div>
                </ContactAccordion>
                {/* Contact Phone/Email Section Accordion */}
                <ContactAccordion label="Contact Info">
                  <div className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-2`}>
                    <div className="w-1/2">
                      <RenderFields
                        fields={phoneFax}
                        formData={formData}
                        handleChange={handleChange}
                        readOnly={!isEditable}
                        validationErrors={validationErrors}
                      />
                    </div>
                    <div className="w-1/2">
                      <RenderFields
                        fields={secondaryPhone}
                        formData={formData}
                        handleChange={handleChange}
                        readOnly={!isEditable}
                        validationErrors={validationErrors}
                      />
                      {isExpanded && (
                        <RenderFields
                          fields={emailContact}
                          formData={formData}
                          handleChange={handleChange}
                          readOnly={!isEditable}
                          validationErrors={validationErrors}
                        />
                      )}
                    </div>
                  </div>
                  {!isExpanded && (
                    <div className="overflow-hidden gap-2">
                      <RenderFields
                        fields={emailContact}
                        formData={formData}
                        handleChange={handleChange}
                        readOnly={!isEditable}
                        validationErrors={validationErrors}
                      />
                    </div>
                  )}
                </ContactAccordion>
              </div>
            </div>
          ) : (
            <p>No contact details available.</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

ContactModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  contactData: PropTypes.shape({}),
  handleDataChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  setNext: PropTypes.func,
  setPrev: PropTypes.func,
};

export default ContactModal;
